import React, { useState, useEffect, useCallback } from 'react';
import { MessageSquare, RefreshCw, User, Bot, Phone, TrendingUp, Loader2, AlertCircle } from 'lucide-react';
import { Button } from '../ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const formatDateBR = (iso) => {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
  } catch { return iso; }
};

const AdminChatbot = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [days, setDays] = useState(7);
  const [selectedSession, setSelectedSession] = useState(null);
  const [modelConfig, setModelConfig] = useState(null);
  const [modelSaving, setModelSaving] = useState(false);

  const fetchModelConfig = useCallback(async () => {
    try {
      const pwd = sessionStorage.getItem('admin_pwd') || '';
      const res = await fetch(`${API_URL}/api/admin/chatbot-config`, {
        headers: { 'Authorization': `Bearer ${pwd}` },
      });
      if (res.ok) setModelConfig(await res.json());
    } catch { /* silent */ }
  }, []);

  useEffect(() => { fetchModelConfig(); }, [fetchModelConfig]);

  const changeModel = async (primary) => {
    setModelSaving(true);
    try {
      const pwd = sessionStorage.getItem('admin_pwd') || '';
      const res = await fetch(`${API_URL}/api/admin/chatbot-config`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${pwd}` },
        body: JSON.stringify({ primary }),
      });
      if (res.ok) setModelConfig(prev => ({ ...prev, primary }));
    } catch { /* silent */ }
    finally { setModelSaving(false); }
  };

  const fetchData = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const pwd = sessionStorage.getItem('admin_pwd') || '';
      const res = await fetch(`${API_URL}/api/admin/chatbot-conversations?days=${days}`, {
        headers: { 'Authorization': `Bearer ${pwd}` },
      });
      if (!res.ok) throw new Error('Erro ao carregar conversas');
      setData(await res.json());
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }, [days]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const topTopics = data
    ? Object.entries(data.topic_counts).sort((a, b) => b[1] - a[1]).filter(([, v]) => v > 0)
    : [];

  return (
    <div className="flex-1 overflow-auto p-4 md:p-6" data-testid="admin-chatbot-tab">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <MessageSquare className="w-5 h-5 text-cyan-400" />
          <h3 className="text-lg font-bold text-white">Conversas do Chatbot IA</h3>
        </div>
        <div className="flex items-center gap-2">
          {modelConfig && (
            <div className="flex items-center gap-1.5">
              <Bot className="w-4 h-4 text-purple-400" />
              <select
                value={modelConfig.primary}
                onChange={e => changeModel(e.target.value)}
                disabled={modelSaving}
                className="bg-slate-800 border border-purple-500/40 text-purple-300 text-sm rounded-lg px-3 py-1.5 outline-none focus:border-purple-400 disabled:opacity-50"
                title="Modelo de IA principal (os outros são fallback automático)"
                data-testid="chatbot-model-select"
              >
                {modelConfig.options.map(o => (
                  <option key={o.id} value={o.id}>{o.label}</option>
                ))}
              </select>
            </div>
          )}
          <select
            value={days}
            onChange={e => setDays(parseInt(e.target.value))}
            className="bg-slate-800 border border-slate-700 text-white text-sm rounded-lg px-3 py-1.5 outline-none focus:border-cyan-500"
            data-testid="chatbot-days-select"
          >
            <option value={1}>Últimas 24h</option>
            <option value={7}>Últimos 7 dias</option>
            <option value={30}>Últimos 30 dias</option>
            <option value={90}>Últimos 90 dias</option>
          </select>
          <Button onClick={fetchData} variant="outline" size="sm" className="border-slate-600 text-slate-300 hover:bg-slate-800">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm flex items-center gap-2">
          <AlertCircle className="w-4 h-4" />{error}
        </div>
      )}

      {loading && !data && (
        <div className="flex items-center justify-center py-20 text-slate-400">
          <Loader2 className="w-6 h-6 animate-spin mr-2" />Carregando...
        </div>
      )}

      {data && (
        <>
          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
            <StatCard icon={MessageSquare} label="Conversas" value={data.total_conversations} color="cyan" testId="stat-conversations" />
            <StatCard icon={Bot} label="Mensagens" value={data.total_messages} color="blue" testId="stat-messages" />
            <StatCard icon={Phone} label="Pediram WhatsApp" value={data.handoff_count} color="emerald" testId="stat-handoff" />
            <StatCard icon={TrendingUp} label="Período" value={`${data.period_days}d`} color="amber" testId="stat-period" />
          </div>

          {/* Top Topics */}
          {topTopics.length > 0 && (
            <div className="mb-5 bg-slate-800/50 border border-slate-700 rounded-xl p-4" data-testid="top-topics">
              <h4 className="text-sm font-semibold text-slate-300 mb-3 flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-cyan-400" />Tópicos mais perguntados
              </h4>
              <div className="flex flex-wrap gap-2">
                {topTopics.map(([topic, count]) => (
                  <span key={topic} className="px-3 py-1 bg-cyan-900/30 border border-cyan-700/50 text-cyan-300 text-xs font-medium rounded-full">
                    {topic} <span className="text-cyan-400 font-bold">· {count}</span>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Layout: Lista + Detalhe */}
          <div className="grid md:grid-cols-[1fr_2fr] gap-4">
            {/* Lista de sessões */}
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl overflow-hidden max-h-[60vh] overflow-y-auto" data-testid="conversations-list">
              {data.conversations.length === 0 ? (
                <div className="p-8 text-center text-slate-500 text-sm">
                  Nenhuma conversa no período selecionado.
                </div>
              ) : (
                data.conversations.map((s) => (
                  <button
                    key={s.session_id}
                    onClick={() => setSelectedSession(s)}
                    className={`w-full text-left px-4 py-3 border-b border-slate-700 hover:bg-slate-800 transition-colors ${
                      selectedSession?.session_id === s.session_id ? 'bg-cyan-900/20 border-l-2 border-l-cyan-400' : ''
                    }`}
                    data-testid={`conversation-${s.session_id}`}
                  >
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <span className="text-xs font-mono text-cyan-400 truncate">{s.session_id.slice(0, 18)}</span>
                      {s.handoff_requested && (
                        <span className="text-[10px] bg-emerald-900/40 border border-emerald-700/50 text-emerald-300 px-1.5 py-0.5 rounded-full shrink-0">
                          📱 WhatsApp
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-white truncate">{s.messages[0]?.user || '-'}</p>
                    <p className="text-[11px] text-slate-500 mt-1">
                      {s.messages.length} msg · {formatDateBR(s.last_at)}
                    </p>
                  </button>
                ))
              )}
            </div>

            {/* Detalhe da conversa */}
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 max-h-[60vh] overflow-y-auto" data-testid="conversation-detail">
              {!selectedSession ? (
                <div className="h-full flex items-center justify-center text-slate-500 text-sm py-12">
                  Selecione uma conversa para ver os detalhes
                </div>
              ) : (
                <div>
                  <div className="flex items-center justify-between mb-3 pb-3 border-b border-slate-700">
                    <div>
                      <p className="text-xs text-slate-400">Sessão</p>
                      <p className="text-sm font-mono text-cyan-400">{selectedSession.session_id}</p>
                    </div>
                    <p className="text-xs text-slate-400">Iniciada em {formatDateBR(selectedSession.started_at)}</p>
                  </div>
                  <div className="space-y-3">
                    {selectedSession.messages.map((m, i) => (
                      <div key={i}>
                        <div className="flex items-start gap-2 mb-2">
                          <div className="bg-blue-500/20 p-1.5 rounded-lg shrink-0"><User className="w-3.5 h-3.5 text-blue-400" /></div>
                          <div className="flex-1">
                            <p className="text-[11px] text-blue-400 font-semibold mb-0.5">Cliente · {formatDateBR(m.at)}</p>
                            <p className="text-sm text-white whitespace-pre-wrap">{m.user}</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-2 ml-4">
                          <div className="bg-cyan-500/20 p-1.5 rounded-lg shrink-0"><Bot className="w-3.5 h-3.5 text-cyan-400" /></div>
                          <div className="flex-1">
                            <p className="text-[11px] text-cyan-400 font-semibold mb-0.5">Agente IA</p>
                            <p className="text-sm text-slate-300 whitespace-pre-wrap">{m.bot}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, color, testId }) => {
  const palette = {
    cyan:    { bg: 'bg-cyan-500/10',    border: 'border-cyan-500/30',    text: 'text-cyan-400'    },
    blue:    { bg: 'bg-blue-500/10',    border: 'border-blue-500/30',    text: 'text-blue-400'    },
    emerald: { bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', text: 'text-emerald-400' },
    amber:   { bg: 'bg-amber-500/10',   border: 'border-amber-500/30',   text: 'text-amber-400'   },
  }[color];
  return (
    <div className={`${palette.bg} ${palette.border} border rounded-xl p-3`} data-testid={testId}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className={`w-4 h-4 ${palette.text}`} />
        <span className="text-[11px] text-slate-400 uppercase tracking-wider">{label}</span>
      </div>
      <p className={`text-2xl font-bold ${palette.text}`}>{value}</p>
    </div>
  );
};

export default AdminChatbot;
